import { useCallback, useEffect, useState } from 'react';

import { useUIState } from '../app/App';

type EventHandlersType = (e: EventPos & { button: number; currentTarget: EventTarget | null }) => void;

const calculateTranslation = (windowMeasure: number, chatMeasure: number, translation: number): number => {
	let calculated: number;
	if (translation < 0) {
		calculated = 0;
	} else if (windowMeasure - chatMeasure < translation) {
		calculated = windowMeasure - chatMeasure;
	} else {
		calculated = translation;
	}

	return calculated;
};

export type UseDragReturnType = {
	onMouseDown: EventHandlersType;
	onMouseMove: EventHandlersType;
	onMouseUp: EventHandlersType;
	position: Pos;
	setPosition: (c: Pos) => void;
};

export const useDrag = (startingPosition: Pos, ref: HTMLDivElement | null, resetPosition: Pos): UseDragReturnType => {
	const {
		clientViewport: { height: windowHeight, width: windowWidth },
	} = useUIState();

	const objectHeight = ref?.clientHeight ?? 0;
	const objectWidth = ref?.clientWidth ?? 0;
	const [dragInfo] = useState({
		isDragging: false,
		origin: { right: 0, bottom: 0 },
		translation: startingPosition,
		lastTranslation: startingPosition,
		windowHeight,
		windowWidth,
		objectHeight,
		objectWidth,
		firstRender: true,
		setPosition: () => void 0,
	}); // мутабельность ради оптимизации

	dragInfo.objectHeight = objectHeight;
	dragInfo.objectWidth = objectWidth;
	const [position, _setPosition] = useState(dragInfo.translation);
	const [setPosition] = useState(() => (p: Pos): void => {
		dragInfo.lastTranslation = p;
		_setPosition(p);
	});

	const onMouseDown: EventHandlersType = useCallback(
		({ clientX, clientY, button }) => {
			if (button === 0) {
				if (!dragInfo.isDragging) dragInfo.isDragging = true;
				dragInfo.origin = { right: clientX, bottom: clientY };
			}
		},
		[dragInfo]
	);

	useEffect((): void => {
		if (dragInfo.firstRender) {
			dragInfo.firstRender = false;
			return;
		}

		setPosition(resetPosition);
	}, [dragInfo, resetPosition, setPosition, windowHeight, windowWidth]);

	const onMouseMove: EventHandlersType = useCallback(
		({ clientX, clientY }) => {
			if (dragInfo.isDragging) {
				const { origin, lastTranslation } = dragInfo;
				const translation = {
					x: -(clientX - (origin.right + lastTranslation.right)),
					y: -(clientY - (origin.bottom + lastTranslation.bottom)),
				};

				const right = calculateTranslation(dragInfo.windowWidth, dragInfo.objectWidth, translation.x);
				const bottom = calculateTranslation(dragInfo.windowHeight, dragInfo.objectHeight, translation.y);

				dragInfo.translation = {
					right,
					bottom,
				};

				_setPosition(dragInfo.translation);
			}
		},
		[dragInfo]
	);

	const onMouseUp: EventHandlersType = useCallback(() => {
		if (dragInfo.isDragging) {
			dragInfo.isDragging = false;
			dragInfo.lastTranslation = { ...dragInfo.translation };
		}
	}, [dragInfo]);

	useEffect(() => {
		window.addEventListener('mouseup', onMouseUp);
		window.addEventListener('mousemove', onMouseMove);

		return (): void => {
			window.removeEventListener('mouseup', onMouseUp);
			window.removeEventListener('mousemove', onMouseMove);
		};
	}, [onMouseMove, onMouseUp]);

	dragInfo.windowHeight = windowHeight;
	dragInfo.windowWidth = windowWidth;
	return {
		position,
		onMouseDown,
		onMouseMove,
		onMouseUp,
		setPosition,
	};
};
